/**
 * Real-time Updates Hook
 *
 * React hook for handling real-time updates via SSE.
 * Includes graceful degradation with polling fallback.
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { sseClient, SSEEvent, EventType } from '../lib/sseClient';
import { useAuth } from '../features/auth';
import { useToast } from '../components/Toast';

/**
 * Hook to manage SSE connection lifecycle
 */
export function useSSEConnection() {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      sseClient.connect();
    } else {
      sseClient.disconnect();
    }

    return () => {
      sseClient.disconnect();
    };
  }, [isAuthenticated]);
}

/**
 * Hook to handle real-time updates for evaluations
 */
export function useRealTimeEvaluations() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubscribe = sseClient.on('evaluation:updated', (event: SSEEvent) => {
      const { evaluationId } = event.payload as { evaluationId: number };
      // Invalidate specific evaluation queries
      queryClient.invalidateQueries({ queryKey: ['evaluations', 'detail', evaluationId] });
      queryClient.invalidateQueries({ queryKey: ['evaluations', 'list'] });
    });

    return unsubscribe;
  }, [queryClient]);

  useEffect(() => {
    const unsubscribe = sseClient.on('evaluation:status_changed', (event: SSEEvent) => {
      const { evaluationId } = event.payload as { evaluationId: number };
      queryClient.invalidateQueries({ queryKey: ['evaluations', 'detail', evaluationId] });
      queryClient.invalidateQueries({ queryKey: ['evaluations', 'list'] });
    });

    return unsubscribe;
  }, [queryClient]);

  useEffect(() => {
    const unsubscribe = sseClient.on('evaluation:score_updated', (event: SSEEvent) => {
      const { evaluationId } = event.payload as { evaluationId: number };
      queryClient.invalidateQueries({ queryKey: ['evaluations', 'detail', evaluationId] });
      queryClient.invalidateQueries({ queryKey: ['evaluations', 'calculation', evaluationId] });
    });

    return unsubscribe;
  }, [queryClient]);
}

/**
 * Hook to subscribe to specific event types
 */
export function useSSEEvent(eventType: EventType, handler: (event: SSEEvent) => void) {
  useEffect(() => {
    const unsubscribe = sseClient.on(eventType, handler);
    return unsubscribe;
  }, [eventType, handler]);
}

/**
 * Hook for toast notifications from SSE
 */
export function useNotificationEvents(onNotification: (message: string, type?: 'success' | 'error' | 'info') => void) {
  const handleNotification = useCallback(
    (event: SSEEvent) => {
      const { message, notificationType } = event.payload as { message: string; notificationType?: 'success' | 'error' | 'info' };
      onNotification(message, notificationType);
    },
    [onNotification]
  );

  useEffect(() => {
    const unsubscribe = sseClient.on('notification', handleNotification);
    return unsubscribe;
  }, [handleNotification]);
}

/**
 * Hook for automatic SSE notifications with toast integration
 */
export function useSSENotifications() {
  const { showToast } = useToast();

  useEffect(() => {
    const unsubscribe = sseClient.on('notification', (event: SSEEvent) => {
      const payload = event.payload as {
        message: string;
        notificationType?: 'success' | 'error' | 'info' | 'warning';
      };
      showToast(payload.message, payload.notificationType || 'info');
    });

    return unsubscribe;
  }, [showToast]);
}

/**
 * Hook for polling fallback when SSE is not available
 */
export function usePollingFallback(
  queryKeys: string[][],
  enabled: boolean = true,
  interval: number = 30000
) {
  const queryClient = useQueryClient();
  const pollingRef = useRef<number | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;

    setIsPolling(true);
    pollingRef.current = window.setInterval(() => {
      queryKeys.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
    }, interval);
  }, [queryClient, queryKeys, interval]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
      setIsPolling(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      stopPolling();
      return;
    }

    // Check SSE connection status and fallback to polling if needed
    const checkConnection = () => {
      if (!sseClient.isConnected()) {
        startPolling();
      } else {
        stopPolling();
      }
    };

    // Initial check
    checkConnection();

    // Periodic check for connection changes
    const checkInterval = setInterval(checkConnection, 10000);

    return () => {
      clearInterval(checkInterval);
      stopPolling();
    };
  }, [enabled, startPolling, stopPolling]);

  return { isPolling, startPolling, stopPolling };
}

/**
 * Hook for SSE connection status
 */
export function useSSEConnectionStatus() {
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const checkConnection = () => {
      setIsConnected(sseClient.isConnected());
    };

    // Initial check
    checkConnection();

    // Periodic check
    const interval = setInterval(checkConnection, 5000);

    return () => clearInterval(interval);
  }, []);

  return isConnected;
}
